import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { deleteCompanyById } from '../../../services/companyServices'
import { useCookies } from 'react-cookie'
import useError from '../../../hooks/useError'
import { companyFront } from '../../../utils/routes.utils'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { AlertTriangle, Lock, Trash2, X, AlertCircle, Building2 } from 'lucide-react'

function DeleteCompany() {
  const { id: companyId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const { error, setError } = useError()
  const [password, setPassword] = useState('')
  const [cookies] = useCookies(['token'])

  // Company model only has userId, no name field

  const handleDeleteCompany = async (e) => {
    e.preventDefault()
    const requiredPassword = process.env.REACT_APP_DELETE_PASSWORD

    if (password === requiredPassword) {
      try {
        setLoading(true)
        setError(null)
        await deleteCompanyById(cookies.token, companyId)
        navigate(`/${companyFront.urlCompanies}`)
      } catch (err) {
        setError('No se pudo eliminar la empresa: ' + err.message)
      } finally {
        setLoading(false)
      }
    } else {
      setError('Contraseña incorrecta')
    }
  }

  const handleCancel = () => {
    navigate(`/${companyFront.urlCompanies}`)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200">
      <Card className="w-full max-w-lg mx-4 shadow-2xl border-destructive">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-2xl">Eliminar Empresa</CardTitle>
                <CardDescription className="mt-1">Esta acción no se puede deshacer</CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Warning Message */}
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-destructive">¿Estás seguro de que deseas eliminar esta empresa?</p>
                <p className="text-muted-foreground">
                  Esta empresa y todos sus datos asociados (clientes, pozos y reportes) serán eliminados permanentemente.
                </p>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}

          {/* Password Form */}
          <form onSubmit={handleDeleteCompany} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">
                Ingresa la contraseña de confirmación
                <span className="text-destructive ml-1">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Contacta al administrador si no conoces la contraseña
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={loading || !password}
                className="min-w-[120px]"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default DeleteCompany
